import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ArrowsClockwise, ArrowUp, ArrowDown,
  MagnifyingGlass, Rocket, Lightning, X
} from "@phosphor-icons/react";

function fmt(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (val >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
  return `$${val.toFixed(2)}`;
}

function fmtPrice(val) {
  if (!val || val === 0) return "$0";
  if (val >= 1000) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (val >= 1) return `$${val.toFixed(2)}`;
  if (val >= 0.001) return `$${val.toFixed(4)}`;
  if (val >= 0.0000001) return `$${val.toFixed(8)}`;
  return `$${val.toExponential(2)}`;
}

function PctChange({ val }) {
  if (val === null || val === undefined) return <span className="text-zinc-600">-</span>;
  const n = parseFloat(val);
  if (isNaN(n)) return <span className="text-zinc-600">-</span>;
  const isUp = n >= 0;
  return (
    <span className={`text-[10px] font-data flex items-center justify-end gap-0.5 ${isUp ? "text-emerald-400" : "text-red-400"}`}>
      {isUp ? <ArrowUp size={8} weight="bold" /> : <ArrowDown size={8} weight="bold" />}
      {Math.abs(n).toFixed(1)}%
    </span>
  );
}

function LiqGauge({ score }) {
  const color = score >= 60 ? "emerald" : score >= 30 ? "amber" : "red";
  return (
    <div className="flex items-center gap-1.5" title={`Liquidity: ${score}/100`}>
      <div className="w-12 bg-zinc-800/60 rounded-full h-1.5 overflow-hidden">
        <div className={`h-full rounded-full bg-${color}-500/70`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
      <span className={`text-[9px] font-data text-${color}-400`}>{score}</span>
    </div>
  );
}

const CHAIN_COLORS = {
  solana: "violet", ethereum: "blue", bsc: "yellow", arbitrum: "blue",
  base: "blue", polygon: "violet", avalanche: "red", optimism: "red",
  pulsechain: "emerald", fantom: "blue",
};

function RiskFlag({ liquidity }) {
  const liq = liquidity || 0;
  if (liq < 100000) return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-sm bg-red-950 text-red-300 tracking-wider">VERY HIGH RISK</span>;
  if (liq < 500000) return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-sm bg-red-900/60 text-red-400 tracking-wider">HIGH RISK</span>;
  if (liq < 1000000) return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-sm bg-yellow-900/40 text-yellow-400 tracking-wider">MEDIUM RISK</span>;
  return <span className="text-[7px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-900/30 text-emerald-400 tracking-wider">LOW RISK</span>;
}

function TokenRow({ token }) {
  const isMeme = token.category === "meme";
  const isBoosted = token.source === "boost";
  const chainColor = CHAIN_COLORS[token.chain] || "zinc";
  return (
    <a
      href={token.url || `https://dexscreener.com/${token.chain}/${token.id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`dex-row-${token.rank}`}
      className="block px-3 py-3 border-b border-zinc-800/30 hover:bg-zinc-900/40 transition-colors"
    >
      <div className="flex items-center gap-2.5">
        <span className="text-[10px] font-data text-zinc-600 w-7 text-center flex-shrink-0">{token.rank}</span>

        {token.image ? (
          <img src={token.image} alt={token.symbol} className="w-8 h-8 rounded-full flex-shrink-0" loading="lazy" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] text-zinc-500 font-bold">{token.symbol?.charAt(0) || "?"}</span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-zinc-100">{token.name}</span>
            {isBoosted && (
              <Rocket size={11} weight="fill" className="text-orange-400 flex-shrink-0" />
            )}
            <Badge className={`text-[6px] px-1 py-0 border-0 rounded-sm h-3 ${
              isMeme ? "bg-amber-900/30 text-amber-400" : "bg-blue-900/30 text-blue-400"
            }`}>
              {isMeme ? "MEME" : "UTILITY"}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-zinc-500 font-data">{token.symbol}</span>
            <span className={`text-[8px] px-1 py-0 bg-${chainColor}-900/30 text-${chainColor}-400 rounded-sm`}>{token.chain}</span>
            {token.dex_id && <span className="text-[8px] text-zinc-600">{token.dex_id}</span>}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          <p className="text-sm font-data text-zinc-200">{fmtPrice(token.current_price)}</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-2 ml-10 flex-wrap">
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Volume</p>
          <p className="text-[11px] font-data text-zinc-300">{fmt(token.volume_24h)}</p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">Liquidity</p>
          <p className="text-[11px] font-data text-zinc-400">{fmt(token.liquidity_usd)}</p>
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">5m</p>
          <PctChange val={token.price_change_5m} />
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">1h</p>
          <PctChange val={token.price_change_1h} />
        </div>
        <div>
          <p className="text-[9px] text-zinc-600 uppercase">24h</p>
          <PctChange val={token.price_change_24h} />
        </div>
        <div>
          <RiskFlag liquidity={token.liquidity_usd} />
        </div>
      </div>
    </a>
  );
}

export default function DexScreenerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [localFilter, setLocalFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const debounceRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const resp = await axios.get(`${API}/dexscreener-tokens`);
      setData(resp.data);
    } catch (e) {
      console.error("DexScreener fetch error:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live DexScreener search with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults(null);
      setSearchMode(false);
      return;
    }
    setSearchMode(true);
    setSearching(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const resp = await axios.get(`${API}/dexscreener-search`, { params: { q: searchQuery } });
        setSearchResults(resp.data);
      } catch (e) {
        console.error("DexScreener search error:", e);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
    setSearchMode(false);
    setSearching(false);
  };

  const allTokens = searchMode ? (searchResults?.tokens || []) : (data?.tokens || []).slice(0, 20);

  return (
    <div className="p-5 max-w-[1400px]" data-testid="dexscreener-page">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <Lightning size={22} weight="bold" className="text-emerald-400" />
            <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">DexScreener</h1>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-emerald-900/30 text-emerald-400 h-4">TOP 20 TRENDING</Badge>
          </div>
          <p className="text-xs text-zinc-500 ml-[34px]">
            Top 20 trending tokens from DexScreener — use search to find any token or pair
          </p>
        </div>
        <button
          data-testid="refresh-dexscreener-btn"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2.5 py-1.5 border border-zinc-800 rounded-sm"
        >
          <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Updating..." : "Refresh"}
        </button>
      </div>

      {/* DexScreener Live Search */}
      <div className="mb-4 bg-zinc-900/50 border border-zinc-800/60 rounded-sm p-3" data-testid="dex-live-search">
        <div className="flex items-center gap-2 mb-2">
          <MagnifyingGlass size={14} weight="bold" className="text-emerald-400" />
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Search DexScreener</span>
          {searchMode && (
            <Badge className="text-[7px] px-1.5 py-0 border-0 bg-emerald-900/30 text-emerald-400 h-4 ml-1">
              LIVE SEARCH
            </Badge>
          )}
        </div>
        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="dex-search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search any token, pair, or contract address on DexScreener..."
            className="w-full text-xs pl-8 pr-20 py-2.5 bg-zinc-950/60 border border-zinc-800/80 rounded-sm text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-emerald-800/60 transition-colors"
          />
          {searching && (
            <div className="absolute right-10 top-1/2 -translate-y-1/2">
              <ArrowsClockwise size={12} className="text-emerald-400 animate-spin" />
            </div>
          )}
          {searchQuery && (
            <button
              data-testid="dex-search-clear"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-zinc-800 transition-colors"
            >
              <X size={12} className="text-zinc-500 hover:text-zinc-300" />
            </button>
          )}
        </div>
        {searchMode && searchResults && (
          <p className="text-[10px] text-zinc-500 mt-1.5">
            Found <span className="text-emerald-400 font-medium">{searchResults.total}</span> pairs for "<span className="text-zinc-300">{searchResults.query}</span>"
          </p>
        )}
      </div>

      {searchMode && (
        <div className="mb-3">
          <button
            data-testid="dex-back-trending"
            onClick={clearSearch}
            className="text-[10px] px-2.5 py-1 rounded-sm border border-zinc-800/60 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors flex items-center gap-1"
          >
            <Lightning size={10} /> Back to Top 20 Trending
          </button>
        </div>
      )}

      {(loading || searching) && !data && (
        <div className="space-y-1">
          {[...Array(15)].map((_, i) => <Skeleton key={i} className="h-10 bg-zinc-900/50" />)}
        </div>
      )}

      {!loading && allTokens.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-sm overflow-hidden" data-testid="dex-table">
          <div className="grid grid-cols-[36px_1.3fr_90px_100px_90px_65px_65px_65px_100px] items-center gap-2 px-3 py-2 border-b border-zinc-800/60 bg-zinc-900/50">
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-center">#</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600">Token</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Price</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">24h Vol</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Liquidity</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">5m</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">1h</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">24h</span>
            <span className="text-[8px] uppercase tracking-wider text-zinc-600 text-right">Risk</span>
          </div>
          <div data-testid="dex-scroll">
            {allTokens.map(token => (
              <TokenRow key={`${token.chain}-${token.id}-${token.rank}`} token={token} />
            ))}
          </div>
        </div>
      )}

      {!loading && allTokens.length === 0 && (
        <div className="text-center py-16">
          <Lightning size={32} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-500">No tokens match your filter</p>
        </div>
      )}

      {data && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800/40">
          <p className="text-[10px] text-zinc-600">
            {allTokens.length} {searchMode ? "search results" : "trending tokens"} shown
          </p>
          <p className="text-[10px] text-zinc-600">
            Source: {data.data_source} | Updated: {new Date(data.fetched_at).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
}
